import { useEffect, useMemo, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import { getCJSSearchUrl } from '../../lib/utils/api.service';
import {
	DATE_FORMATS,
	DATEPICKER_ERROR_MESSAGE,
	DATE_MODIFIERS,
	TYPEAHEAD_MESSAGES
} from '../../lib/utils/constants';
import { RoomComposition, Rooms } from '../../lib/utils/composition.service';
import handleInputFocus from '../../lib/utils/handle-input-focus';
import { useApplication } from '../../hooks/useApplication';
import {
	CJSSearchRequest,
	Gateway,
	LocationParams,
	Location
} from '../../types/api';
import { BookingType } from '../../types/config';
import {
	FLIGHT_HOTEL,
	HOLIDAY,
	HOTEL,
	PRODUCT_TYPES
} from '../../types/criteria';
import SeasonalMessageComponent from '../seasonal-message/seasonal-message.component';
import RoomFieldComponent from '../room-field/room-field.component';
import DatePickerFieldComponent from '../date-picker-field/date-picker-field.component';
import TypeaheadComponent from '../typeahead/typeahead.component';
import ListFieldComponent from '../list-field/list-field.component';

import {
	ButtonComponent,
	ButtonSize,
	ButtonType
} from '@vaa-component-lib/component.atom.button';
import {
	AlertComponent,
	AlertEmphasis,
	AlertStatus
} from '@vaa-component-lib/component.molecule.alert';
import {
	IconActionSearchComponent,
	IconTravelGlobeMarkerComponent,
	IconTravelTakeOffComponent
} from '@vaa-component-lib/component.atom.icons';

import {
	Div,
	FormGrid,
	column,
	destination,
	date,
	passengers,
	readOnly,
	ServerError,
	hotelSubmit,
	hotelInput,
	submit
} from './forms.styles';

import { inputWidths, hotelInputWidths, mainColumn } from './holiday.styles';
import HolidayNavComponent from '../holiday-nav/holiday-nav.component';
import HolidayInfoComponent from '../holiday-info/holiday-info.component';
import { refsToUrlParams } from '../../lib/utils/refs';
interface HolidayComponentProps {
	bookingType: BookingType;
	selectSubBookingType: any;
}

const HolidayComponent = ({
	bookingType,
	selectSubBookingType
}: HolidayComponentProps) => {
	const { application } = useApplication();
	const [submitting, setSubmitting] = useState<boolean>(false);
	const ref = useRef<HTMLDivElement>(null);
	const criteria =
		bookingType.type === application?.criteria?.searchType
			? application?.criteria
			: null;

	const formMethods = useForm({
		mode: 'onBlur',
		reValidateMode: 'onChange'
	});

	if (criteria && !criteria?.refs?.['refUrl']) {
		criteria.refs = {};
		criteria.refs['refUrl'] = 'VA_NHP_SEARCH';
	}

	const [gateways, setGateways] = useState<Gateway[] | []>(
		(criteria as FLIGHT_HOTEL | HOLIDAY)?.gateways || []
	);

	const {
		register,
		control,
		setError,
		handleSubmit,
		formState: { isSubmitting, errors },
		watch
	} = formMethods;

	const onSubmit = (data: any, error: any) => {
		setSubmitting(true);
		const query: CJSSearchRequest = {
			brand: bookingType?.brand,
			submissionType:
				bookingType?.type === PRODUCT_TYPES.FLIGHT_HOTEL
					? PRODUCT_TYPES.HOLIDAY
					: data.submissionType,
			location: data.location.locationUrlDescription,
			departureDate: DateTime.fromISO(data.departing[0]).toFormat(
				DATE_FORMATS.URL_DATE
			),
			duration: DateTime.fromISO(data.departing[1]).diff(
				DateTime.fromISO(data.departing[0]),
				'days'
			).days,
			partyCompositions: Rooms.format(data.partyCompositions),
			...(data.gateway && { gateway: data.gateway })
		};

		getCJSSearchUrl(query)
			.then((link) => {
				const url = `${link.data.url}${refsToUrlParams(criteria?.refs)}`;
				window.open(url, '_self');
			})
			.catch((error) => {
				setSubmitting(false);
				setError('root.serverError', {
					type: 'custom',
					message:
						"Oops! We're having some technical issues, please pop back later."
				});
			});
	};

	const watchLocation: Location = watch('location');
	const flightPlusParams: LocationParams = {
		brand: bookingType?.brand,
		bookingType:
			bookingType.type === PRODUCT_TYPES.FLIGHT_HOTEL
				? PRODUCT_TYPES.HOLIDAY
				: bookingType?.type
	};

	/*
		By Default:
		* The start date will always be two days from today.
		* The end date will always be 24 months from the start date.

		When onSaleStart and onSaleEnd are passed:
		* We use these dates, if they are not in the past.
		* If either date is in the past:
			- If only onSaleStart is in the past and durationValue and durationUnit are provided
				then we calculate the end date as the onSaleStart plus duration.
			- If both onSaleStart and onSaleEnd are in the past then the location should not be
				available to be selected (this is filtered out by the Validate Locations service)

		When durationValue and durationUnit are passed (without onSaleStart):
				* We calculate the end date based on the duration and onSaleStart (if valid),
					or the default start date.
				* If this is in the past then we revert to defaults.

		Search team tickets #641926, #641929, #642823

		- JK
	*/

	const enabledDateRange = useMemo(() => {
		const { onSaleStart, onSaleEnd, durationValue, durationUnit } =
			watchLocation?.locationMetadata || {};
		const today = DateTime.now().startOf('day');

		const defaultStart = today.plus({
			days: DATE_MODIFIERS.holidaysStartDays
		});
		const defaultEnd = defaultStart.plus({
			months: DATE_MODIFIERS.holidaysEndMonths
		});

		let calculatedStartDate = onSaleStart
			? DateTime.fromISO(onSaleStart)
			: defaultStart;

		let calculatedEndDate = onSaleEnd
			? DateTime.fromISO(onSaleEnd)
			: defaultEnd;

		if (durationValue && durationUnit) {
			let duration = {};
			switch (durationUnit) {
				case 'd':
					duration = { days: durationValue };
					break;
				case 'w':
					duration = { weeks: durationValue };
					break;
				case 'm':
					duration = { months: durationValue };
					break;
				case 'y':
					duration = { years: durationValue };
					break;
				default:
					calculatedStartDate = defaultStart;
					calculatedEndDate = defaultEnd;
			}

			if (Object.keys(duration).length > 0) {
				calculatedEndDate = calculatedStartDate.plus(duration);

				if (calculatedEndDate < today) {
					calculatedStartDate = defaultStart;
					calculatedEndDate = defaultEnd;
				}
			}
		}

		if (calculatedEndDate < today) {
			calculatedStartDate = defaultStart;
			calculatedEndDate = defaultEnd;
		}

		if (calculatedStartDate > calculatedEndDate) {
			calculatedStartDate = defaultStart;
			calculatedEndDate = defaultEnd;
		}

		return [calculatedStartDate.toISO(), calculatedEndDate.toISO()];
	}, [
		watchLocation?.locationMetadata?.onSaleStart,
		watchLocation?.locationMetadata?.onSaleEnd,
		watchLocation?.locationMetadata?.durationValue,
		watchLocation?.locationMetadata?.durationUnit
	]);

	useEffect(() => {
		if (bookingType.type === PRODUCT_TYPES.HOTEL) return;
		if (watchLocation?.gateways) {
			setGateways(watchLocation?.gateways);
			return;
		}
		watchLocation
			? setGateways(watchLocation?.locationMetadata?.gateway)
			: setGateways([]);
	}, [watchLocation]);

	return (
		<form
			id={bookingType?.type?.toString()}
			data-testid={`search-form-${bookingType?.type?.toString()}`}
			onSubmit={handleSubmit(onSubmit)}>
			<FormProvider {...formMethods}>
				{errors?.root?.serverError?.type === 'custom' && (
					<ServerError>
						<AlertComponent
							emphasis={AlertEmphasis.Low}
							hasIcon
							status={AlertStatus.Error}
							title={errors?.root?.serverError?.message}>
							{''}
						</AlertComponent>
					</ServerError>
				)}
				<SeasonalMessageComponent />
				<HolidayNavComponent
					brand={bookingType.brand}
					selectedBookingType={bookingType}
					change={selectSubBookingType}
				/>
				<FormGrid>
					<Div
						css={[
							column,
							mainColumn,
							bookingType.type === PRODUCT_TYPES.HOTEL
								? hotelInput(inputWidths)
								: destination(inputWidths)
						]}>
						<Controller
							render={({ field: { ref, ...rest }, fieldState }) => (
								<TypeaheadComponent
									label={'Destination'}
									popoverTitle={'Destination'}
									placeholder={'e.g. Orlando'}
									historyTitle={TYPEAHEAD_MESSAGES.historyTitle}
									noResultsLabel={TYPEAHEAD_MESSAGES.noResultsMatch}
									iconChild={<IconTravelGlobeMarkerComponent />}
									id={'flight_hotel_destination'}
									feedback={errors?.location?.message?.toString()}
									hasError={errors?.location ? true : false}
									switchFocusTo={() =>
										handleInputFocus(
											bookingType?.type === PRODUCT_TYPES.HOTEL
												? 'flight_hotel_departing'
												: 'flight_hotel_gateway'
										)
									}
									locationSearch={flightPlusParams}
									{...rest}
									{...fieldState}
								/>
							)}
							name={'location'}
							defaultValue={
								(criteria as FLIGHT_HOTEL | HOLIDAY | HOTEL)?.location || ''
							}
							control={control}
							rules={{
								required: TYPEAHEAD_MESSAGES.requiredDestination
							}}
						/>
					</Div>
					{bookingType.type !== PRODUCT_TYPES.HOTEL && (
						<Div css={[column, mainColumn, destination(inputWidths), readOnly]}>
							<Controller
								render={({ field: { ref, ...rest }, fieldState }) => (
									<ListFieldComponent
										label={'Flying from'}
										placeholder={''}
										id={'flight_hotel_gateway'}
										iconChild={<IconTravelTakeOffComponent />}
										feedback={errors?.gateway?.message?.toString()}
										hasError={Boolean(errors?.gateway)}
										options={gateways?.map((gateway) => ({
											label: gateway.description,
											value: gateway.code
										}))}
										isDisabled={!watchLocation}
										switchFocusTo={() =>
											handleInputFocus('flight_hotel_departing')
										}
										{...rest}
										{...fieldState}
									/>
								)}
								name={'gateway'}
								defaultValue={
									(criteria as FLIGHT_HOTEL | HOLIDAY)?.gateway || null
								}
								control={control}
								rules={{
									required: 'Please select where you are flying from'
								}}
							/>
						</Div>
					)}

					{/* ***** DATEPICKER ****** */}
					<Div
						ref={ref}
						css={[
							column,
							mainColumn,
							bookingType.type === PRODUCT_TYPES.HOTEL
								? hotelInput(inputWidths)
								: date(inputWidths),
							readOnly
						]}>
						<Controller
							render={({ field: { ref, ...rest }, fieldState }) => (
								<DatePickerFieldComponent
									label={'When'}
									id={'flight_hotel_departing'}
									feedback={errors?.departing?.message?.toString()}
									hasError={Boolean(errors?.departing)}
									bookingType={bookingType?.type}
									enabledDateRange={enabledDateRange}
									{...rest}
									{...fieldState}
								/>
							)}
							name={'departing'}
							defaultValue={[
								...((criteria as FLIGHT_HOTEL | HOLIDAY | HOTEL)?.departureDate
									?.isValid
									? [
											(criteria as FLIGHT_HOTEL | HOLIDAY | HOTEL)
												?.departureDate,
											(
												criteria as FLIGHT_HOTEL | HOLIDAY | HOTEL
											)?.departureDate.plus({
												days: (criteria as FLIGHT_HOTEL | HOLIDAY | HOTEL)
													?.duration
											})
										]
									: [])
							]}
							control={control}
							rules={{
								validate: {
									requiredDate: (value) => {
										const duration = (
											criteria as FLIGHT_HOTEL | HOLIDAY | HOTEL
										)?.duration;

										if (
											duration > 28 ||
											(value.length > 1 &&
												DateTime.fromISO(value[1]).diff(
													DateTime.fromISO(value[0]),
													'days'
												).days > 28)
										) {
											return DATEPICKER_ERROR_MESSAGE.duration;
										}
										if (value.length < 1)
											return DATEPICKER_ERROR_MESSAGE.noSelection;
										if (value.length !== 2)
											return DATEPICKER_ERROR_MESSAGE.range;
									}
								}
							}}
						/>
					</Div>
					{/* ***** DATEPICKER ****** */}

					<Div
						css={[
							column,
							mainColumn,
							bookingType.type === PRODUCT_TYPES.HOTEL
								? hotelInput(inputWidths)
								: passengers(inputWidths),
							readOnly
						]}>
						<Controller
							render={({ field: { ref, ...rest }, fieldState }) => (
								<RoomFieldComponent
									label={'Who / Rooms'}
									placeholder={'Who?'}
									id={'flight_hotel_who'}
									feedback={errors?.partyCompositions?.message?.toString()}
									hasError={Boolean(errors?.partyCompositions)}
									{...rest}
									{...fieldState}
								/>
							)}
							name={'partyCompositions'}
							defaultValue={
								(criteria as FLIGHT_HOTEL | HOLIDAY | HOTEL)?.rooms
									? (criteria as FLIGHT_HOTEL | HOLIDAY | HOTEL)?.rooms
									: null
							}
							control={control}
							rules={{
								required: 'This field is required',
								validate: (value: RoomComposition[]) => {
									const messages = [];
									const adults = value.reduce(
										(total, composition) => (total += composition.adults),
										0
									);
									const infants = value.reduce(
										(total, composition) => (total += composition.infants),
										0
									);
									const noAge = value.reduce(
										(total, composition) =>
											(total += composition.childAges.filter(
												(age) => !age
											).length),
										0
									);
									const total = value.reduce(
										(total, composition) =>
											(total +=
												composition.adults +
												composition.childAges.length +
												composition.infants),
										0
									);
									const rooms = value.length;
									if (noAge > 0) {
										messages.push('Please enter all child ages.');
									}
									if (infants > adults) {
										messages.push(
											'There must be at least one adult per child under two years of age.'
										);
									}
									if (total > 9) {
										messages.push(
											'We love a group! For bookings of more than 9 passengers please call our specialists on 0344 472 1422.'
										);
									}
									if (rooms > 4) {
										messages.push(
											'Whoops! Looks like you have more than 4 rooms. Please remove any extra rooms.'
										);
									}
									return messages.length <= 0 ? true : messages.join('\n');
								}
							}}
						/>
					</Div>

					<Div
						css={[
							column,
							mainColumn,
							bookingType.type === PRODUCT_TYPES.HOTEL
								? hotelSubmit(hotelInputWidths)
								: submit(inputWidths)
						]}>
						<ButtonComponent
							data-cy="submit-button"
							type={ButtonType.Submit}
							disabled={isSubmitting}
							size={ButtonSize.Medium}
							iconChild={<IconActionSearchComponent />}
							text={'Search'}
							isLoading={submitting}
							loadingText="Search"
						/>
					</Div>
				</FormGrid>
				<input
					readOnly
					type="hidden"
					defaultValue={bookingType?.type}
					{...register('submissionType')}
				/>
				<input
					readOnly
					type="hidden"
					defaultValue={application?.brand}
					{...register('brand')}
				/>
				<HolidayInfoComponent />
			</FormProvider>
		</form>
	);
};

export default HolidayComponent;
